import { EventBus } from '@/event-bus';
import store from '@/store';

const waitForConnection = function (ws, callback, interval) {
  if (ws.readyState === 1) {
    callback();
  } else {
    setTimeout(function () {
      waitForConnection(ws, callback, interval);
    }, interval);
  }
};

const state = {
  socket: null,
  lastEventReceivedByControllerId: {},
  isConnected: false,
};

const getters = {
  getLastEventReceivedForControllerId: (state) => (controllerId) => {
    return state.lastEventReceivedByControllerId[controllerId] ?? null;
  },
  isConnected: (state) => state.isConnected,
};

const actions = {
  createFacilitySocketConnection({ state, commit, rootState }, facilityId) {
    if (facilityId) {
      commit('createFacilitySocketConnection', facilityId);
    }
  },
  send({ state }, message) {
    waitForConnection(
      state.socket,
      () => {
        state.socket.send(message);
      },
      1000
    );
  },
};

const initializeSocket = (socket, state) => {
  let env = process.env;
  socket.facilitySocket = new WebSocket(
    `${env.VUE_APP_trimaster_websockets}/ws/facility${socket.facilityId}?userId=${store.getters.getUser.id}`
  );

  socket.startReconnection = () => {
    if (socket.reconnectionTimer) return; // Already trying to reconnect

    socket.reconnectionAttempts = 0;

    const attemptReconnection = () => {
      if (state.isConnected) {
        socket.stopReconnection();
        return;
      }

      socket.reconnectionAttempts++;
      const delay = Math.min(1000 * Math.pow(2, socket.reconnectionAttempts - 1), 30000); // Exponential backoff, max 30 seconds

      console.log(`Attempting to reconnect WebSocket (attempt ${socket.reconnectionAttempts})...`);
      initializeSocket(socket, state);

      // Schedule next attempt with exponential backoff
      socket.reconnectionTimer = setTimeout(attemptReconnection, delay);
    };

    // Start first attempt immediately
    attemptReconnection();
  };

  socket.stopReconnection = () => {
    if (socket.reconnectionTimer) {
      clearInterval(socket.reconnectionTimer);
      socket.reconnectionTimer = null;
      socket.reconnectionAttempts = 0;
    }
  };

  socket.facilitySocket.onerror = function (event) {
    console.error('WebSocket error observed:', event);
    state.isConnected = false;
    socket.startReconnection();
  };
  socket.facilitySocket.onclose = function (event) {
    console.error('WebSocket close observed:', event);
    state.isConnected = false;
    socket.startReconnection();
  };
  socket.facilitySocket.onopen = () => {
    console.info('WebSocket open observed');
    state.isConnected = true;
    socket.stopReconnection();
    socket.pong();
  };
  socket.facilitySocket.onmessage = (message) => {
    if (message.data === '__ping__') {
      socket.ping();
      return;
    }

    const messageData = JSON.parse(message.data);
    EventBus.$emit('facility-websocket-message', messageData);
    switch (messageData.type) {
      case 'reportState':
      case 'setDeviceParameter':
      case 'setLogLevel':
      case 'reportSoilSensorReading':
      case 'photocapture':
        break;
      case 'reportReading':
      case 'reportSensorReading':
      case 'sensorNameChanged':
        store.dispatch('sensorReadings/updateReading', messageData);
        break;
      case 'reportBatteryLevel':
        store.dispatch('sensorReadings/updateBattery', messageData);
        break;
      case 'sensorPriorityChanged':
        store.dispatch('sensorReadings/updateSensorPriority', messageData);
        break;
      case 'unpublishedDeviceMessage':
      default:
        return;
    }
    const data = messageData.payload;
    if (data.controllerId) {
      state.lastEventReceivedByControllerId[data.controllerId] = data;
    }
  };
  return socket;
};

const mutations = {
  createFacilitySocketConnection(state, facilityId) {
    if (state.socket) {
      state.socket.facilitySocket.close();
      state.socket.stopReconnection();
    }
    let socket = {};
    socket.facilityId = facilityId;
    socket.heartbeat = null;
    socket.reconnectionTimer = null;
    socket.reconnectionAttempts = 0;
    socket.pong = () => {
      socket.heartbeat = setTimeout(function () {
        // We haven't received a ping for a while, need to restart connection
        socket.facilitySocket.close();
        initializeSocket(socket, state);
        EventBus.$emit('facility-websocket-restart');
      }, 45000);
    };
    socket.ping = () => {
      clearTimeout(socket.heartbeat);
      socket.pong();
    };
    socket = initializeSocket(socket, state);
    state.socket = socket;
  },
  setIsConnected(state, isConnected) {
    state.isConnected = isConnected;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
